import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CheckCircle, Clock, FileDown, Image, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { KpiTile } from '@/components/viz/Kpi'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { getDashboard } from '../api/dashboardApi'
import { GrowthHero } from './GrowthHero'
import {
  ArtistPulse, AttentionStrip, CatalogueGrowth, CurationQueue, Panel, PublishingHealth, SalesHero, SplitBar,
  SupportLoad, TopSellers, Unavailable,
} from './panels'

function greeting(now = new Date()) {
  const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour: 'numeric', hourCycle: 'h23' }).format(now))
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
}

export function ManagerDashboard() {
  const user = useAuthStore(state => state.user)
  const query = useQuery({ queryKey: ['admin-dashboard'], queryFn: getDashboard, refetchInterval: 60_000 })
  const report = query.data
  const today = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', weekday: 'long', month: 'long', day: 'numeric' }).format(new Date())
  const catalogue = report?.catalogue
  const published = catalogue?.byStatus.PUBLISHED ?? { total: 0, imported: 0 }
  const pending = catalogue?.byStatus.PENDING ?? { total: 0, imported: 0 }

  return <div className="space-y-6 p-6 md:p-8">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="text-sm text-muted-foreground">{today}</p>
        <h1 className="font-serif text-4xl font-semibold tracking-tight">{greeting()}{user?.firstName ? `, ${user.firstName}` : ''}</h1>
        <p className="mt-1 text-muted-foreground">What needs you, and how the platform is doing.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={() => void query.refetch()} disabled={query.isFetching}>
          <RefreshCw className={cn('mr-2 h-4 w-4', query.isFetching && 'animate-spin')} />Refresh
        </Button>
        <Button asChild variant="outline"><Link to="/app/export-artworks"><FileDown className="mr-2 h-4 w-4" />Export Data</Link></Button>
      </div>
    </div>

    {query.isError && <div role="alert" className="rounded-lg border border-destructive/30 p-4 text-sm">The dashboard could not be loaded. Try refreshing.</div>}
    {query.isLoading && <div role="status" aria-label="Loading dashboard" className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map(i => <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />)}</div>
      <div className="h-80 animate-pulse rounded-xl bg-muted" />
    </div>}

    {report && <>
      <section aria-labelledby="attention-heading" className="space-y-3">
        <h2 id="attention-heading" className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Needs attention</h2>
        <AttentionStrip report={report} />
      </section>

      {report.growth ? <GrowthHero growth={report.growth} />
        : report.sales ? <SalesHero sales={report.sales} /> : <Panel title="Sales"><Unavailable what="Sales" /></Panel>}

      {catalogue && <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile label="Artworks" value={catalogue.total} icon={<Image className="h-5 w-5" />} delay={60}
          detail="Live catalogue, excluding disabled">
          <SplitBar parts={[
            { label: 'Artist', value: catalogue.total - catalogue.imported, color: 'var(--viz-uploads)' },
            { label: 'Pictufy', value: catalogue.imported, color: 'var(--viz-imports)' },
          ]} />
        </KpiTile>
        <KpiTile label="Published" value={published.total} icon={<CheckCircle className="h-5 w-5" />} delay={120}
          detail={`${catalogue.total ? Math.round(100 * published.total / catalogue.total) : 0}% of the catalogue is on sale`}>
          <SplitBar parts={[
            { label: 'Artist', value: published.total - published.imported, color: 'var(--viz-uploads)' },
            { label: 'Pictufy', value: published.imported, color: 'var(--viz-imports)' },
          ]} />
        </KpiTile>
        <KpiTile label="Pending review" value={pending.total} icon={<Clock className="h-5 w-5" />} delay={180}
          detail={report.approvals ? `${report.approvals.waiting.toLocaleString()} waiting · ${report.approvals.held.toLocaleString()} on hold` : 'Submitted for approval'}>
          {report.approvals && <SplitBar parts={[
            { label: 'Waiting', value: report.approvals.waiting, color: 'var(--viz-warning)' },
            { label: 'On hold', value: report.approvals.held, color: 'hsl(var(--muted-foreground))' },
          ]} />}
        </KpiTile>
        <KpiTile label="Drafts" value={catalogue.byStatus.DRAFT?.total ?? 0} delay={240}
          detail="Uploaded but not yet submitted by the artist">
          <Link to="/app/artworks" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground">Browse artworks<ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
        </KpiTile>
      </div>}

      <div className="grid items-start gap-6 xl:grid-cols-3">
        <Panel className="xl:col-span-2" delayMs={80} title="Catalogue growth" description="Artworks added per day over the last 30 days, Central time.">
          {catalogue ? <CatalogueGrowth catalogue={catalogue} /> : <Unavailable what="The catalogue" />}
        </Panel>
        <Panel delayMs={140} title="Top sellers" description="By revenue from fulfilled orders, last 30 days.">
          {report.sales ? <TopSellers sales={report.sales} /> : <Unavailable what="Sales" />}
        </Panel>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <Panel delayMs={80} title="Publishing" description="Publish tasks per day, last 14 days."
          action={<Button asChild variant="ghost" size="sm"><Link to="/app/tasks">Task queue<ArrowRight className="ml-1 h-4 w-4" /></Link></Button>}>
          {report.publishing ? <PublishingHealth publishing={report.publishing} /> : <Unavailable what="Publishing" />}
        </Panel>
        <Panel delayMs={140} title="Support" description="Open tickets now; opened and resolved per day, last 14 days."
          action={<Button asChild variant="ghost" size="sm"><Link to="/app/support">Inbox<ArrowRight className="ml-1 h-4 w-4" /></Link></Button>}>
          {report.support ? <SupportLoad support={report.support} /> : <Unavailable what="Support" />}
        </Panel>
        <Panel className="lg:col-span-2 xl:col-span-1" delayMs={200} title="Artists" description="Observed Easel use, last 30 days. Pictufy catalog accounts are not counted."
          action={<Button asChild variant="ghost" size="sm"><Link to="/app/artist-activity">Activity<ArrowRight className="ml-1 h-4 w-4" /></Link></Button>}>
          <ArtistPulse />
        </Panel>
      </div>

      <Panel delayMs={80} title="Curation queue" description="The longest-waiting submissions.">
        {report.approvals ? <CurationQueue approvals={report.approvals} /> : <Unavailable what="The approval queue" />}
      </Panel>

      <p className="text-xs text-muted-foreground">Refreshes every minute. Updated {new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' }).format(new Date(report.generatedAt))} Central time.</p>
    </>}
  </div>
}
